import { toaster } from '@/components/ui/toaster';
import { useAuth } from '@/contexts/auth-context';
import { announcementsQueryKey } from '@/hooks/queries/use-announcements-query';
import { Button } from '@chakra-ui/react';
import { RefreshDouble } from 'iconoir-react';
import { useInstagramStore } from '@/stores/integrations/instagram-store';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import InstagramPageSelectModal from './InstagramPageSelectModal';
import IntegrationCard from './IntegrationCard';
import type { IntegrationCardVariant } from './IntegrationCard';

interface InstagramIntegrationProps {
  id: string | null;
  isConnected: boolean;
  metadata?: {
    name?: string;
    username?: string;
  };
  variant?: IntegrationCardVariant;
  detailHref?: string;
}

interface InstagramPage {
  id: string;
  name: string;
  accessToken: string;
}

/**
 * Instagram Integration Component
 *
 * Handles Instagram Business account connection using Facebook OAuth Authorization Code flow with PKCE.
 * The authorization code flow provides better security by using server-side token exchange
 * instead of exposing tokens in browser URLs.
 */
const InstagramIntegration = (props: InstagramIntegrationProps) => {
  const { t } = useTranslation();
  const { id, isConnected, metadata, variant, detailHref } = props;
  const { user } = useAuth();
  const brandId = user?.currentBrand?.id;
  const queryClient = useQueryClient();

  const [showPageSelect, setShowPageSelect] = useState(false);
  const [pages, setPages] = useState<InstagramPage[]>([]);
  // Which click-initiated action is in flight, so each button shows its own
  // spinner. The store's isLoading is shared across connect/disconnect, so it
  // alone cannot tell the Reconnect and Disconnect buttons apart.
  const [pendingAction, setPendingAction] = useState<'oauth' | 'disconnect' | null>(null);
  const { isLoading, setLoading, disconnect, initiateOAuthFlow, exchangeCodeForToken } =
    useInstagramStore();

  const refreshConnectionState = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['integrations'] });
    if (brandId) {
      queryClient.invalidateQueries({ queryKey: announcementsQueryKey(brandId) });
    }
  }, [brandId, queryClient]);

  /**
   * Handle the authorization code from Facebook OAuth callback.
   * Exchanges the authorization code for access tokens and processes Instagram pages.
   */
  const handleAuthorizationCode = useCallback(
    async (code: string, state: string) => {
      if (!brandId) {
        toaster.create({
          description: t('integrations.instagram.errors.noBrand'),
          type: 'error',
        });
        return;
      }

      try {
        setLoading(true);

        // Exchange authorization code for access token and get pages
        const result = await exchangeCodeForToken(code, state, brandId);

        if (result.pages && result.pages.length > 0) {
          // Show page selection modal
          setPages(result.pages);
          // No need to store a general access token since pages have their own tokens
          setShowPageSelect(true);
        } else {
          toaster.create({
            description: t('integrations.instagram.errors.noAccounts'),
            type: 'error',
          });
        }
      } catch (error) {
        console.error('Failed to process authorization code:', error);

        // Provide more specific error messages based on error type
        let errorMessage = t('integrations.instagram.errors.authorizationFailed');
        if (error instanceof Error) {
          if (error.message.includes('Invalid state parameter')) {
            errorMessage = t('integrations.instagram.errors.securityCheckFailed');
          } else if (error.message.includes('No PKCE parameters')) {
            errorMessage = t('integrations.instagram.errors.sessionExpired');
          }
        }

        toaster.create({
          description: errorMessage,
          type: 'error',
        });
      } finally {
        setLoading(false);
      }
    },
    [brandId, setLoading, exchangeCodeForToken, t]
  );

  useEffect(() => {
    // Check for authorization code in URL query parameters
    const urlParams = new URLSearchParams(window.location.search);
    const code = urlParams.get('code');
    const state = urlParams.get('state');
    const error = urlParams.get('error');
    const errorDescription = urlParams.get('error_description');

    // Handle OAuth errors from Facebook
    if (error) {
      console.error('OAuth error:', error, errorDescription);
      toaster.create({
        description: errorDescription || `Authorization failed: ${error}`,
        type: 'error',
        duration: 10000,
      });

      // Clean up URL parameters
      const newUrl = window.location.pathname;
      window.history.replaceState({}, '', newUrl);
      return;
    }

    // Handle successful authorization code response (skip TikTok and Meta Ads
    // callbacks, which land on the same page and are owned by their own cards)
    if (code && state && !state.startsWith('tt_') && !state.startsWith('ma_')) {
      handleAuthorizationCode(code, state);

      // Clean up URL parameters after processing
      const newUrl = window.location.pathname;
      window.history.replaceState({}, '', newUrl);
      return;
    }

    // Handle legacy success/error query params for backward compatibility
    const legacyState = urlParams.get('state');
    const message = urlParams.get('message');
    const service = urlParams.get('service');

    // Handle legacy success for Instagram connection
    if (legacyState === 'success' && service === 'instagram') {
      refreshConnectionState();
      toaster.create({
        description: t('integrations.instagram.success.connected'),
        type: 'success',
      });

      // Clean up URL parameters
      const newUrl = window.location.pathname;
      window.history.replaceState({}, '', newUrl);
    }

    // Handle legacy error scenario
    if (legacyState === 'error' && message) {
      toaster.create({
        description: `Error: ${decodeURIComponent(message)}`,
        type: 'error',
        duration: 10000,
      });

      // Clean up URL parameters
      const newUrl = window.location.pathname;
      window.history.replaceState({}, '', newUrl);
    }
  }, [brandId, handleAuthorizationCode, refreshConnectionState, t]);

  /**
   * Start (or restart) the Facebook OAuth flow. Reused by both connect and
   * reconnect: the callback upserts the integration, so reconnecting refreshes
   * the token and merges any newly requested scopes (e.g. ads permissions)
   * without disconnecting first.
   */
  const startOAuth = useCallback(async () => {
    setPendingAction('oauth');
    try {
      await initiateOAuthFlow();
      toaster.create({
        description: t('integrations.instagram.redirecting'),
        type: 'loading',
      });
    } catch (error) {
      console.error('Failed to initiate OAuth flow:', error);
      toaster.create({
        description: t('integrations.instagram.errors.connectionFailed'),
        type: 'error',
      });
      // On success the page redirects away; only a failure needs to clear this.
      setPendingAction(null);
    }
  }, [initiateOAuthFlow, t]);

  const handleToggle = async () => {
    if (isConnected && id) {
      // Handle disconnection
      setPendingAction('disconnect');
      try {
        await disconnect(id);
        // The card reads the server, so the row only flips once the refreshed
        // list says so.
        refreshConnectionState();
        toaster.create({
          description: t('integrations.instagram.success.disconnected'),
          type: 'success',
        });
      } catch (error) {
        console.error('Failed to disconnect Instagram:', error);
        toaster.create({
          description: t('integrations.instagram.errors.disconnectFailed'),
          type: 'error',
        });
      } finally {
        setPendingAction(null);
      }
    } else {
      await startOAuth();
    }
  };

  return (
    <>
      <IntegrationCard
        name={t('integrations.instagram.name')}
        description={t('integrations.instagram.description')}
        icon="https://img.freepik.com/psd-gratuit/logo-application-instagram_23-2151544096.jpg?semt=ais_hybrid"
        isActive={isConnected}
        isLoading={pendingAction === (isConnected ? 'disconnect' : 'oauth')}
        disabled={isLoading || pendingAction !== null}
        onToggle={handleToggle}
        connectedAs={metadata?.username || metadata?.name}
        connectedLabel={t('integrations.connectedLabel')}
        variant={variant}
        detailHref={detailHref}
        inlineAction={
          <Button
            variant="outline"
            colorPalette="gray"
            size="sm"
            onClick={startOAuth}
            disabled={isLoading || pendingAction !== null}
            loading={pendingAction === 'oauth'}
          >
            <RefreshDouble width={16} height={16} />
            {t('integrations.card.reconnect')}
          </Button>
        }
      />
      {brandId && (
        <InstagramPageSelectModal
          isOpen={showPageSelect}
          onClose={() => setShowPageSelect(false)}
          pages={pages}
          brandId={brandId}
          onSuccess={refreshConnectionState}
        />
      )}
    </>
  );
};

export default InstagramIntegration;
